import { Object3D } from '@wonderlandengine/api';
import { NetworkedComponent, networkManager } from '@wonderlandcloud/client';
import Logger from '../utils/logger.js';
import { EToyDefinition, EToyType } from './etoy-types.js';
import { EToy } from './etoy.js';
import { NetworkedGrabbable } from '../networked-grabbable.js';

const MODULE = 'ToyManager';

export interface ToyContext {
    toys: Map<number, any>;
    toyInstances: Map<number, Object3D>;
    toyBallPrefab?: Object3D | null;
    toyProximityVibePrefab?: Object3D | null;
    toyOscillatingRotPrefab?: Object3D | null;
    playerUserId: number;
    registerNetworkedGrabbable: (networkId: number, component: NetworkedGrabbable) => void;
    unregisterNetworkedGrabbable: (networkId: number) => void;
}

export function registerToy(context: ToyContext, toy: EToyDefinition): void {
    context.toys.set(toy.networkId, toy);

    const physxOwnership = (toy.physxOwner === context.playerUserId);

    const transform = Array.isArray(toy.transform) && toy.transform.length >= 7
        ? toy.transform
        : [0, 0, 0, 1, 0, 1, 0, 0];

    const existing = context.toyInstances.get(toy.networkId);
    if (existing) {
        const netComp = existing.getComponent(NetworkedGrabbable);
        netComp?.managePhysxOwnership(toy);
        applyToyTransform(existing, transform);
        return;
    }

    const prefab = getToyPrefab(context, toy.type);
    if (!prefab) {
        Logger.warn(`No prefab configured for toy type: ${toy.type}`, MODULE);
        return;
    }

    const instance = prefab.clone();
    if (!instance) {
        Logger.warn(`Failed to clone prefab for toy type: ${toy.type}`, MODULE);
        return;
    }

    const grabbable = instance.getComponent(NetworkedGrabbable);
    grabbable?.managePhysxOwnership(toy);
    applyToyTransform(instance, transform);

    if (grabbable) {
        grabbable.networkId = toy.networkId;
        grabbable.completeInitialization(physxOwnership);
        context.registerNetworkedGrabbable(toy.networkId, grabbable);
    } else {
        const modeString: string = (physxOwnership) ? 'send' : 'receive';
        const netComp = instance.getComponent(NetworkedComponent) ?? instance.addComponent(NetworkedComponent, {
            networkId: toy.networkId,
            mode: modeString,
        });
        if (netComp) {
            netComp.networkId = toy.networkId;
            (netComp as any).mode = modeString;
            networkManager.registerNetworkedComponent(netComp, physxOwnership);
        }
    }

    const toyComp = instance.getComponents();
    for (const comp of toyComp) {
        if (comp instanceof EToy) {
            comp.active = true;
        }
    }
    context.toyInstances.set(toy.networkId, instance);
    Logger.info(`Spawned toy ${toy.type} with networkId ${toy.networkId}`, MODULE);
}

export function removeToy(context: ToyContext, networkId: number): void {
    const instance = context.toyInstances.get(networkId);
    if (instance && !instance.isDestroyed) {
        const grab = instance.getComponent(NetworkedGrabbable);
        if (grab) {
            context.unregisterNetworkedGrabbable(networkId);
        }
        networkManager.removeObject(networkId);
        instance.destroy();
    }
    context.toyInstances.delete(networkId);
    context.toys.delete(networkId);
    Logger.info(`Removed toy networkId ${networkId}`, MODULE);
}

export function removeAllToys(context: ToyContext, ids?: number[]): void {
    const targets = ids ?? Array.from(context.toyInstances.keys());
    for (const id of targets) {
        removeToy(context, id);
    }
}

function getToyPrefab(context: ToyContext, type: EToyType | string): Object3D | null {
    const normalized = String(type).toLowerCase();
    switch (normalized) {
        case EToyType.BALL:
            return context.toyBallPrefab ?? null;
        case EToyType.PROXIMITY_VIBE:
            return context.toyProximityVibePrefab ?? null;
        case EToyType.OSCILLATING_ROT:
            return context.toyOscillatingRotPrefab ?? null;
        default:
            return null;
    }
}

function applyToyTransform(instance: Object3D, transform: number[]): void {
    const rot = transform.length >= 4 ? transform.slice(0, 4) : [0, 0, 0, 1];

    // Properly extract translation from dual quaternion: t = 2 * qd * qr^-1
    let pos: number[];
    if (transform.length >= 8) {
        const qr = { x: rot[0], y: rot[1], z: rot[2], w: rot[3] };
        const qd = { x: transform[4], y: transform[5], z: transform[6], w: transform[7] };

        // Conjugate of qr (for unit quaternions, conjugate = inverse)
        const qrConj = { x: -qr.x, y: -qr.y, z: -qr.z, w: qr.w };

        // Multiply: qd * qrConj
        const temp = {
            w: qd.w * qrConj.w - qd.x * qrConj.x - qd.y * qrConj.y - qd.z * qrConj.z,
            x: qd.w * qrConj.x + qd.x * qrConj.w + qd.y * qrConj.z - qd.z * qrConj.y,
            y: qd.w * qrConj.y - qd.x * qrConj.z + qd.y * qrConj.w + qd.z * qrConj.x,
            z: qd.w * qrConj.z + qd.x * qrConj.y - qd.y * qrConj.x + qd.z * qrConj.w
        };

        // Multiply by 2 to get translation
        pos = [temp.x * 2, temp.y * 2, temp.z * 2];
    } else {
        pos = [0, 1, 0];
    }

    instance.setRotationWorld(new Float32Array(rot));
    instance.setPositionWorld(new Float32Array(pos));
}
